import React, { useMemo } from 'react';

import { useClickTracking } from '@lumapps/analytics-tracking/hooks/useClickTracking';
import { useImpressionTracking } from '@lumapps/analytics-tracking/hooks/useImpressionTracking';
import { TargetType } from '@lumapps/analytics-tracking/types';
import { MAX_IMAGE_SIZE } from '@lumapps/constants';
import { useImageLightboxProvider } from '@lumapps/lumx-images/hooks/useImageLightboxProvider';
import { Alignment, AspectRatio, ImageBlock, ImageBlockCaptionPosition } from '@lumapps/lumx/react';
import { retrieveKeyFromServeImageUrl } from '@lumapps/medias/utils';
import { resizeImage } from '@lumapps/medias/utils/resizeImage';
import { sanitizeHTML } from '@lumapps/utils/string/sanitizeHtml';
import { unescapeHTML } from '@lumapps/utils/string/unescapeHTML';
import { BlockComponent } from '@lumapps/widget-base/types';

import { BlockImagePreview as BlockImagePreviewProps } from '../../types';

import './index.scss';

const ASPECT_RATIO_MAP: Record<BlockImagePreviewProps['imageFill'], AspectRatio> = {
    original: AspectRatio.original,
    width: AspectRatio.horizontal,
    height: AspectRatio.vertical,
};

const CAPTION_POSITION_MAP: Record<BlockImagePreviewProps['captionPosition'], ImageBlockCaptionPosition> = {
    below: ImageBlockCaptionPosition.below,
    cover: ImageBlockCaptionPosition.over,
};

const CLASSNAME = 'block-image-preview';
export const BlockImagePreview: BlockComponent<BlockImagePreviewProps> = ({
    captionPosition,
    description,
    name,
    imageFill,
    image,
    theme,
}) => {
    // Unescape, so that & that have been converted to &amp; are displayed as &
    const sanitizedDescription = unescapeHTML(sanitizeHTML(description));
    const alt = image.alt || sanitizedDescription || '';

    const imageFullSize = useMemo(
        () => [
            {
                title: name,
                description: sanitizedDescription,
                alt,
                image: resizeImage(image.url, MAX_IMAGE_SIZE),
            },
        ],
        [name, sanitizedDescription, alt, image.url],
    );

    const { getTriggerProps } = useImageLightboxProvider(imageFullSize);

    const mediaId = retrieveKeyFromServeImageUrl(image.url)?.split('-')[1] ?? image.url;

    const impressionsRef = useImpressionTracking(mediaId, TargetType.MEDIA);
    // onClick only (not ref): the thumbnail wrapper is already clickable via getTriggerProps's own
    // onClick, and attaching the tracking ref to the same node would double-fire the click event.
    const { onClick: onClickTracking } = useClickTracking(mediaId, TargetType.MEDIA);

    const triggerProps = getTriggerProps?.();

    return (
        <ImageBlock
            ref={impressionsRef}
            className={CLASSNAME}
            captionPosition={CAPTION_POSITION_MAP[captionPosition]}
            description={sanitizedDescription}
            image={image.url}
            theme={theme}
            title={name}
            alt={alt}
            align={Alignment.center}
            thumbnailProps={{
                aspectRatio: ASPECT_RATIO_MAP[imageFill],
                ...triggerProps,
                style: { width: '100%' },
                onClick: triggerProps?.onClick
                    ? (event?: React.MouseEvent) => {
                          triggerProps.onClick(event);
                          onClickTracking();
                      }
                    : undefined,
            }}
        />
    );
};
